// Chained-transaction invariants shared by every flow that signs a parent
// and a child together (setup → purchase, setup → mints, commit → reveal,
// wrap → swap).
//
// The rule: a child built before its parent is signed can only reference
// the parent by a txid that signing will not move. That holds for native
// SegWit and Taproot inputs (the txid preimage excludes the witness), holds
// for nested P2SH-P2WPKH only once the redeem-script push is in scriptSig,
// and never holds for legacy P2PKH, whose scriptSig carries the signature
// itself. Every provider finalizes a little differently, so the one thing
// callers can rely on is the SIGNED parent's raw bytes. Check the link
// against those, before anything is broadcast.

import * as bitcoin from 'bitcoinjs-lib'
import { finalTxidFromPsbt, parsePsbt } from '@/utils/market/secure-psbt'

const PSBT_MAGIC = '70736274ff'

export class ChainMismatchError extends Error {
	constructor(
		public readonly label: string,
		public readonly signedParentTxid: string,
		public readonly referenced: string[]
	) {
		super(
			`${label}: signing changed a transaction id — nothing was broadcast. ` +
				`The next transaction expected ${referenced.join(', ') || 'no parent'}, ` +
				`but the signed one is ${signedParentTxid}. Please retry.`
		)
		this.name = 'ChainMismatchError'
	}
}

/** txid of a signed raw transaction. */
export function txidOfRawTx(rawtx: string): string {
	return bitcoin.Transaction.fromHex(rawtx).getId()
}

/** True when `hex` is a PSBT rather than a raw transaction. */
export function isPsbtHex(hex: string): boolean {
	return hex.toLowerCase().startsWith(PSBT_MAGIC)
}

/**
 * The txid a PSBT's transaction will have once signed, when that is
 * knowable. Nested P2SH-P2WPKH inputs get their redeem-script push; native
 * SegWit and Taproot need nothing. Throws for a legacy (or unrecognised)
 * input, because no pre-sign txid exists for it and chaining on a guess
 * would strand the child.
 */
export function presignTxidFromPsbt(psbtHex: string): string {
	const psbt = parsePsbt(psbtHex)
	psbt.data.inputs.forEach((input, index) => {
		const script = input.witnessUtxo?.script
			? Buffer.from(input.witnessUtxo.script)
			: input.nonWitnessUtxo
			? bitcoin.Transaction.fromBuffer(Buffer.from(input.nonWitnessUtxo)).outs[
					psbt.txInputs[index].index
			  ]?.script
			: undefined
		if (!script) {
			throw new Error(`input ${index}: no prevout to derive the pre-sign txid from`)
		}
		const bytes = Buffer.from(script)
		const p2tr = bytes.length === 34 && bytes[0] === 0x51 && bytes[1] === 0x20
		const p2wpkh = bytes.length === 22 && bytes[0] === 0x00 && bytes[1] === 0x14
		const p2sh = bytes.length === 23 && bytes[0] === 0xa9 && bytes[1] === 0x14
		if (!p2tr && !p2wpkh && !p2sh) {
			throw new Error(
				`input ${index}: legacy or unsupported funding script — this transaction cannot be chained before signing`
			)
		}
		if (p2sh && !(input.redeemScript && input.redeemScript.length)) {
			throw new Error(`input ${index}: nested P2SH-P2WPKH input is missing its redeem script`)
		}
	})
	return finalTxidFromPsbt(psbtHex)
}

/** Every previous-output txid a child (PSBT or raw tx) spends. */
export function inputTxids(childHex: string): string[] {
	if (isPsbtHex(childHex)) {
		return parsePsbt(childHex).txInputs.map((input) =>
			Buffer.from(input.hash).reverse().toString('hex')
		)
	}
	return bitcoin.Transaction.fromHex(childHex).ins.map((input) =>
		Buffer.from(input.hash).reverse().toString('hex')
	)
}

/**
 * Assert that `child` (a signed raw tx or a PSBT) spends the SIGNED parent.
 *
 * `expectedParentTxids` are the ids the child may have been built against
 * (the server's pre-sign id, the unsigned id): if the child references one
 * of those instead of the signed id, signing moved the txid and the chain
 * is broken. Throws `ChainMismatchError`; callers must not have broadcast
 * anything yet.
 */
export function assertChainLinks(
	label: string,
	signedParentRawtx: string,
	childHex: string,
	expectedParentTxids: string[] = []
): void {
	const signed = txidOfRawTx(signedParentRawtx)
	const referenced = inputTxids(childHex)
	if (referenced.includes(signed)) return
	const stale = new Set(expectedParentTxids.map((t) => t.toLowerCase()))
	const wrong = referenced.filter((t) => stale.has(t))
	throw new ChainMismatchError(label, signed, wrong.length ? wrong : referenced)
}

/**
 * Re-point a child PSBT at the signed parent: every input that spent one of
 * `staleParentTxids` (the ids the child was built against) now spends the
 * signed parent's txid and carries the signed parent as `nonWitnessUtxo`.
 * This is the one place the "setup txid patch" lives; providers that sign
 * the parent first should use it rather than copying the loop.
 */
export function linkChildToSignedParent(
	signedParentRawtx: string,
	childPsbtHex: string,
	staleParentTxids: string[]
): string {
	const signedTx = bitcoin.Transaction.fromHex(signedParentRawtx)
	const signedHash = signedTx.getHash()
	const stale = new Set(staleParentTxids.map((t) => t.toLowerCase()))
	const psbt = parsePsbt(childPsbtHex)
	const rebuilt = new bitcoin.Psbt({ network: bitcoin.networks.bitcoin })
	rebuilt.setVersion(psbt.version)
	rebuilt.setLocktime(psbt.locktime)
	psbt.txInputs.forEach((input, index) => {
		const txid = Buffer.from(input.hash).reverse().toString('hex')
		const data = { ...psbt.data.inputs[index] }
		const hash = stale.has(txid) ? signedHash : Buffer.from(input.hash)
		if (stale.has(txid)) {
			// Carry the signed parent as the input's previous transaction, but
			// only when it agrees with the witnessUtxo the child already has:
			// PSBT parsers reject a pair that disagree, and a segwit input is
			// signable from the witnessUtxo alone.
			const prevout = signedTx.outs[input.index]
			const witness = data.witnessUtxo
			const agrees =
				!!prevout &&
				(!witness ||
					(witness.value === prevout.value &&
						Buffer.from(witness.script).equals(Buffer.from(prevout.script))))
			if (agrees) {
				data.nonWitnessUtxo = signedTx.toBuffer()
			}
		}
		rebuilt.addInput({ hash, index: input.index, sequence: input.sequence, ...data })
	})
	psbt.txOutputs.forEach((output, index) => {
		rebuilt.addOutput({ script: Buffer.from(output.script), value: output.value })
		const data = psbt.data.outputs[index]
		if (data && Object.keys(data).length) rebuilt.updateOutput(index, data)
	})
	return rebuilt.toHex()
}
